import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, Store } from 'lucide-react'

import { createStudioAssetListing, type ProductionAsset } from '@/api/studio-production'
import { resolveApiErrorMessage } from '@/features/studio/lib/resolveApiError'
import { ProtoBtn, ProtoInput, ProtoLabel, ProtoModal, ProtoTextarea } from '@/features/studio/components/ui-kit'

export function AssetListingModal({
  open,
  onClose,
  productionId,
  asset,
}: {
  open: boolean
  onClose: () => void
  productionId: string
  asset: ProductionAsset | null
}) {
  const { t } = useTranslation()
  const [price, setPrice] = useState('500')
  const [summary, setSummary] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setPrice('500')
    setSummary('')
    setError(null)
  }, [open, asset?.id])

  async function handleSubmit() {
    if (!asset) return
    const parsed = Number(price)
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setError(t('assets.listingPriceInvalid'))
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      const res = await createStudioAssetListing(productionId, asset.id, {
        title: asset.name,
        summary: summary.trim(),
        price_points: Math.floor(parsed),
        license_mode: 'non_exclusive',
      })
      const sellUrl = `/marketplace?tab=sell&listing=${encodeURIComponent(res.listing_public_id)}`
      window.location.assign(sellUrl)
    } catch (e) {
      setError(resolveApiErrorMessage(e, 'assets.listingFailed'))
      setSubmitting(false)
    }
  }

  return (
    <ProtoModal
      open={open}
      onClose={onClose}
      title={t('assets.listingTitle')}
      footer={
        <>
          <ProtoBtn size="sm" variant="secondary" onClick={onClose} disabled={submitting}>
            {t('assets.listingCancel')}
          </ProtoBtn>
          <ProtoBtn size="sm" disabled={submitting || !asset} onClick={() => void handleSubmit()}>
            {submitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Store className="h-4 w-4" />
            )}
            {t('assets.listingConfirm')}
          </ProtoBtn>
        </>
      }
    >
      <p className="mb-4 rounded-lg border border-emerald-500/20 bg-emerald-500/5 px-3 py-2 text-[11px] text-emerald-200/90">
        {t('assets.listingHint')}
      </p>
      {asset ? (
        <p className="mb-3 text-sm text-zinc-300">{asset.name}</p>
      ) : null}
      {error ? <p className="mb-3 text-sm text-rose-300">{t(error, { defaultValue: error })}</p> : null}
      <div className="space-y-3">
        <div>
          <ProtoLabel required>{t('assets.listingPrice')}</ProtoLabel>
          <ProtoInput type="number" min={1} value={price} onChange={(e) => setPrice(e.target.value)} />
        </div>
        <div>
          <ProtoLabel>{t('assets.listingSummary')}</ProtoLabel>
          <ProtoTextarea rows={2} value={summary} onChange={(e) => setSummary(e.target.value)} />
        </div>
      </div>
    </ProtoModal>
  )
}
